const deliverySection = document.querySelector('.delivery-sec');

if (deliverySection) {
  const DELIVERY_SCROLL_OFFSET = 130;
  const deliveryCards = Array.from(deliverySection.querySelectorAll('.delivery-card'));
  let layoutFrameId = null;
  let layoutTimeoutId = null;
  let resizeTimeoutId = null;

  const getCardParts = (card) => ({
    expandable: card.querySelector('.delivery-card__expandable'),
    expandableInner: card.querySelector('.delivery-card__expandable-inner'),
    toggleButton: card.querySelector('.delivery-card__toggle'),
  });

  const updateCardSwipers = (card) => {
    if (!card) {
      return;
    }

    const swiperElements = card.querySelectorAll('.benefits-swiper-wr .swiper, .delivery-stories-swiper-wr .swiper');

    swiperElements.forEach((swiperElement) => {
      const swiperInstance = swiperElement.swiper;

      if (!swiperInstance) {
        return;
      }

      swiperInstance.updateSize();
      swiperInstance.updateSlides();
      swiperInstance.updateProgress();
      swiperInstance.updateSlidesClasses();
      swiperInstance.update();

      if (typeof swiperInstance.updateAutoHeight === 'function') {
        swiperInstance.updateAutoHeight(0);
      }
    });
  };

  const setExpandableHeight = (card, { animate = true } = {}) => {
    const { expandable, expandableInner } = getCardParts(card);

    if (!expandable || !expandableInner) {
      return;
    }

    const nextHeight = card.classList.contains('is-active')
      ? `${Math.ceil(expandable.scrollHeight)}px`
      : '0px';

    if (!animate) {
      const previousTransition = expandable.style.transition;
      expandable.style.transition = 'none';
      expandable.style.maxHeight = nextHeight;
      expandable.offsetHeight;
      expandable.style.transition = previousTransition;
      return;
    }

    requestAnimationFrame(() => {
      expandable.style.maxHeight = nextHeight;
    });
  };

  const syncActiveCardLayout = ({ animate = false, updateSwiper = true } = {}) => {
    deliveryCards.forEach((card) => {
      if (!card.classList.contains('is-active')) {
        return;
      }

      setExpandableHeight(card, { animate });

      if (updateSwiper) {
        updateCardSwipers(card);
      }
    });
  };

  const scheduleActiveCardLayout = ({ immediate = false } = {}) => {
    if (layoutFrameId) {
      cancelAnimationFrame(layoutFrameId);
    }

    clearTimeout(layoutTimeoutId);

    if (immediate) {
      syncActiveCardLayout({ animate: false, updateSwiper: true });
      return;
    }

    layoutFrameId = requestAnimationFrame(() => {
      syncActiveCardLayout({ animate: false, updateSwiper: true });

      layoutTimeoutId = window.setTimeout(() => {
        syncActiveCardLayout({ animate: false, updateSwiper: true });
      }, 260);
    });
  };

  const setActiveCard = (nextCard, { animate = true } = {}) => {
    if (!nextCard) {
      return;
    }

    deliveryCards.forEach((card) => {
      const isActive = card === nextCard;
      card.classList.toggle('is-active', isActive);

      const { toggleButton } = getCardParts(card);
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      setExpandableHeight(card, { animate });
    });

    scheduleActiveCardLayout();
  };

  const getDeliveryScrollOffset = (offset) => {
    const parsedOffset = Number(offset || 0);
    const header = document.querySelector('.header');
    const headerOffset = header
      ? Math.ceil(header.getBoundingClientRect().height) + 32
      : DELIVERY_SCROLL_OFFSET;

    return Math.max(Number.isFinite(parsedOffset) ? parsedOffset : 0, headerOffset);
  };

  const openDeliveryCard = (target, { scroll = false, offset = 0, duration = 600 } = {}) => {
    const targetCard = typeof target === 'string'
      ? deliverySection.querySelector(target)
      : target;

    if (!targetCard || !targetCard.classList.contains('delivery-card')) {
      return false;
    }

    setActiveCard(targetCard, { animate: false });
    scheduleActiveCardLayout({ immediate: true });

    if (scroll) {
      const targetSelector = `#${targetCard.id}`;
      const scrollOffset = getDeliveryScrollOffset(offset);

      requestAnimationFrame(() => {
        if (typeof window.scrollToEl === 'function') {
          window.scrollToEl(`${targetSelector}|${duration}|${scrollOffset}`);
          return;
        }

        const targetTop = targetCard.getBoundingClientRect().top + window.pageYOffset - scrollOffset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
    }

    return true;
  };

  const shouldIgnoreCardClick = (event) => {
    const interactiveSelector = 'a, button, input, select, textarea, label, summary, [role="button"], [data-no-card-toggle]';

    return Boolean(event.target.closest(interactiveSelector));
  };

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target.closest('.delivery-card');

        if (!card || !card.classList.contains('is-active')) {
          return;
        }

        scheduleActiveCardLayout({ immediate: true });
      });
    })
    : null;

  deliveryCards.forEach((card) => {
    const { toggleButton, expandableInner } = getCardParts(card);

    card.classList.remove('is-active');

    if (resizeObserver && expandableInner) {
      resizeObserver.observe(expandableInner);
    }

    card.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', () => {
        if (!card.classList.contains('is-active')) {
          return;
        }

        scheduleActiveCardLayout({ immediate: true });
      });
    });

    if (!toggleButton) {
      return;
    }

    toggleButton.setAttribute('aria-expanded', 'false');

    card.addEventListener('click', (event) => {
      if (card.classList.contains('is-active') || shouldIgnoreCardClick(event)) {
        return;
      }

      setActiveCard(card);
    });

    toggleButton.addEventListener('click', (event) => {
      event.preventDefault();

      if (card.classList.contains('is-active')) {
        return;
      }

      setActiveCard(card);
    });
  });

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeoutId);
    resizeTimeoutId = window.setTimeout(() => {
      scheduleActiveCardLayout({ immediate: true });
    }, 120);
  }, { passive: true });

  window.addEventListener('load', () => {
    scheduleActiveCardLayout({ immediate: true });
  }, { once: true });

  window.openDeliveryCard = openDeliveryCard;

  window.addEventListener('delivery-card:open', (event) => {
    if (!event.detail?.target) {
      return;
    }

    openDeliveryCard(event.detail.target, event.detail);
  });

  deliveryCards.forEach((card) => {
    setExpandableHeight(card, { animate: false });
  });

  if (window.location.hash && window.location.hash.startsWith('#speciality_')) {
    openDeliveryCard(window.location.hash, { scroll: true, offset: DELIVERY_SCROLL_OFFSET });
  }
}
